import api from '../client';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

/**
 * Service du module RAG (Gemini File Search).
 * Le chat utilise `fetch` (et non axios) pour lire le flux SSE en streaming.
 */
const ragService = {
  // ---- Documents ----
  listDocuments: async (params = {}) => {
    const res = await api.get('/rag/documents', { params });
    return res.data;
  },

  uploadDocument: async (file, { tags = [], author = '' } = {}, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (tags.length) formData.append('tags', tags.join(','));
    if (author) formData.append('author', author);

    const res = await api.post('/rag/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return res.data;
  },

  deleteDocument: async (id) => {
    const res = await api.delete(`/rag/documents/${id}`);
    return res.data;
  },

  // ---- Recherche ----
  search: async (q, documentId) => {
    const res = await api.get('/rag/search', { params: { q, documentId } });
    return res.data;
  },

  // ---- Chat ----
  listConversations: async () => {
    const res = await api.get('/rag/chat/conversations');
    return res.data;
  },

  getMessages: async (conversationId) => {
    const res = await api.get(`/rag/chat/conversations/${conversationId}/messages`);
    return res.data;
  },

  /**
   * Envoie un message et streame la réponse.
   * @param {object} payload { message, conversationId?, documentId? }
   * @param {object} handlers { onConversation, onDelta, onCitations, onError, onDone }
   * @returns {AbortController} pour annuler le flux
   */
  sendMessageStream: ({ message, conversationId, documentId }, handlers = {}) => {
    const controller = new AbortController();
    const token = localStorage.getItem('accessToken');

    (async () => {
      try {
        const response = await fetch(`${API_BASE}/rag/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ message, conversationId, documentId }),
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`Erreur serveur (${response.status})`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n\n');
          buffer = lines.pop() || ''; // garde le fragment incomplet

          for (const block of lines) {
            const line = block.trim();
            if (!line.startsWith('data:')) continue;
            const payload = line.slice(5).trim();
            if (payload === '[DONE]') {
              handlers.onDone?.();
              return;
            }
            try {
              const event = JSON.parse(payload);
              if (event.type === 'conversation') handlers.onConversation?.(event.conversationId);
              else if (event.type === 'delta') handlers.onDelta?.(event.text);
              else if (event.type === 'citations') handlers.onCitations?.(event.citations);
              else if (event.type === 'error') handlers.onError?.(event.message);
            } catch {
              /* ignore lignes non-JSON */
            }
          }
        }
        handlers.onDone?.();
      } catch (err) {
        if (err.name !== 'AbortError') handlers.onError?.(err.message);
      }
    })();

    return controller;
  },

  // ---- Admin ----
  getMetrics: async () => {
    const res = await api.get('/rag/admin/metrics');
    return res.data;
  },
};

export default ragService;
